import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import {
  AppointmentConcern,
  AppointmentDeliveryOption,
  BookingFlowOption,
  UserRoleEnum,
} from 'generated/api';
import AppointmentUtils from 'services/AppointmentUtils';
import Icon from 'components/Utils/NewIcon';

export interface BookingConcernPickerSelection {
  optionId?: string;
  deliveryOption?: any;
  // Set by the cascade when a staff user picks a concern the patient isn't
  // normally eligible for (gender/age/subscription). Drives the override
  // confirmation; stripped before reaching the consumer's onChange.
  ineligibleReason?: string;
}

interface BookingConcernPickerProps {
  patientId: string;
  value?: string;
  onChange: (concernId: string, selection?: BookingConcernPickerSelection) => void;
  label?: string;
  width?: string;
  withDeliveryOptions?: boolean;
  deliveryValue?: string;
  deliveryTelemedicineMethod?: string;
}

interface TreeNode {
  option: BookingFlowOption;
  children: TreeNode[];
  concerns: AppointmentConcern[];
}

function buildBookingOptionTree(options: BookingFlowOption[]): TreeNode[] {
  const byParent = new Map<string, BookingFlowOption[]>();
  options.forEach((o) => {
    const parent = o.child_of || '__root__';
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent)!.push(o);
  });
  const build = (parentId: string): TreeNode[] =>
    (byParent.get(parentId) || [])
      .slice()
      .sort((a, b) => (parseInt(String(a.order)) || 0) - (parseInt(String(b.order)) || 0))
      .map((option) => ({
        option,
        children: build(option.id!),
        concerns: option.concerns || [],
      }));
  return build('__root__');
}

function findConcernAndOption(
  options: BookingFlowOption[],
  id: string | undefined,
): { concern: AppointmentConcern; option: BookingFlowOption } | null {
  if (!id) return null;
  for (const option of options) {
    for (const concern of option.concerns || []) {
      if (concern.id === id) return { concern, option };
    }
  }
  return null;
}

function concernMinutes(concern: AppointmentConcern): number | undefined {
  const c: any = concern;
  return c.capacity_duration_minutes || c.minutes;
}

function formatConcernLabel(concern: AppointmentConcern): string {
  const minutes = concernMinutes(concern);
  return minutes ? `${concern.text} (${minutes} min)` : concern.text || '';
}

function formatDeliveryLabel(opt: AppointmentDeliveryOption): string {
  if (!opt) return '';
  return opt.telemedicine_method || opt.type!;
}

const BookingConcernPicker: FC<BookingConcernPickerProps> = ({
  patientId,
  value,
  onChange,
  label = 'Appointment Concern',
  width = '100%',
  withDeliveryOptions = false,
  deliveryValue,
  deliveryTelemedicineMethod,
}) => {
  const dispatch = useAppDispatch();
  const options = useAppSelector((s) => s.appointments.options);
  const concernRequirements = useAppSelector((s) => s.concernRequirements.byId);
  const patient = useAppSelector((s) => s.patients.byId[patientId]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingPick, setPendingPick] = useState<{
    concernId: string;
    selection?: BookingConcernPickerSelection;
  } | null>(null);

  useEffect(() => {
    if (!options || options.length === 0) {
      dispatch(api.Appointments.getBookingFlowOptions({ patientId }));
    }
    if (!concernRequirements || Object.keys(concernRequirements).length === 0) {
      dispatch(api.Appointments.getConcernRequirements());
    }
  }, [dispatch, patientId]);

  const availableOptionIds = useMemo(
    () =>
      patient
        ? AppointmentUtils.getAvailableBookingOptionIds(options || [], patient, concernRequirements)
        : new Set<string>(),
    [options, patient, concernRequirements],
  );

  const tree = useMemo(() => buildBookingOptionTree(options || []), [options]);

  const selected = useMemo(() => findConcernAndOption(options || [], value), [options, value]);

  const selectedDeliveryOption = useMemo(() => {
    if (!withDeliveryOptions || !selected || !deliveryValue) return null;
    // Telemedicine options share type 'Video' and differ only by telemedicine_method,
    // so match on both; fall back to type-only when no method is known.
    const opts = (selected.concern.delivery_options || []) as any[];
    return (
      opts.find(
        (o) => o.type === deliveryValue && o.telemedicine_method === deliveryTelemedicineMethod,
      ) ||
      opts.find((o) => o.type === deliveryValue) ||
      null
    );
  }, [withDeliveryOptions, selected, deliveryValue, deliveryTelemedicineMethod]);

  const triggerLabel = useMemo(() => {
    if (!selected) return ' ';
    const concernPart = formatConcernLabel(selected.concern);
    if (selectedDeliveryOption) {
      return `${concernPart} · ${formatDeliveryLabel(selectedDeliveryOption)}`;
    }
    return concernPart;
  }, [selected, selectedDeliveryOption]);

  const handleClose = () => setAnchorEl(null);

  // Strip the override flag before handing the selection to the consumer.
  const commitPick = (concernId: string, selection?: BookingConcernPickerSelection) => {
    if (!selection) {
      onChange(concernId);
      return;
    }
    const { ineligibleReason, ...rest } = selection;
    onChange(concernId, rest);
  };

  const handlePick = (concernId: string, selection?: BookingConcernPickerSelection) => {
    setAnchorEl(null);
    // Ineligible concern picked by staff: confirm the override first.
    if (selection?.ineligibleReason) {
      setPendingPick({ concernId, selection });
      return;
    }
    commitPick(concernId, selection);
  };

  return (
    <FormControl className="default-select-field" style={{ width }}>
      <InputLabel shrink>{label}</InputLabel>
      <div
        className="booking-concern-trigger"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{
          marginTop: 16,
          paddingBottom: 7,
          borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          minHeight: 19,
        }}
      >
        <span style={{ flex: 1 }}>{triggerLabel}</span>
        <span style={{ marginLeft: 8, opacity: 0.6 }}>▾</span>
      </div>
      {anchorEl && (
        <ConcernCascadeLevel
          isRoot
          anchorEl={anchorEl}
          onClose={handleClose}
          childNodes={tree}
          concerns={[]}
          parentOptionId={undefined}
          patient={patient}
          concernRequirements={concernRequirements}
          availableOptionIds={availableOptionIds}
          onPick={handlePick}
          withDeliveryOptions={withDeliveryOptions}
        />
      )}
      {pendingPick && (
        <Dialog open onClose={() => setPendingPick(null)}>
          <DialogTitle>Concern not normally available</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {pendingPick.selection?.ineligibleReason} This concern isn&apos;t normally available
              to this patient. Book it anyway?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <DefaultButton
              name="Cancel"
              className="red"
              onClick={() => setPendingPick(null)}
              style={{ width: '150px', marginRight: '20px' }}
            />
            <DefaultButton
              name="Book anyway"
              className="blue"
              onClick={() => {
                commitPick(pendingPick.concernId, pendingPick.selection);
                setPendingPick(null);
              }}
            />
          </DialogActions>
        </Dialog>
      )}
    </FormControl>
  );
};

interface ConcernCascadeLevelProps {
  isRoot: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  childNodes: TreeNode[];
  concerns: AppointmentConcern[];
  parentOptionId: string | undefined;
  patient: any;
  concernRequirements: Record<string, any>;
  availableOptionIds: Set<string>;
  onPick: (concernId: string, selection?: BookingConcernPickerSelection) => void;
  withDeliveryOptions: boolean;
}

const ConcernCascadeLevel: FC<ConcernCascadeLevelProps> = ({
  isRoot,
  anchorEl,
  onClose,
  childNodes,
  concerns,
  parentOptionId,
  patient,
  concernRequirements,
  availableOptionIds,
  onPick,
  withDeliveryOptions,
}) => {
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  const [openConcernId, setOpenConcernId] = useState<string | null>(null);
  const folderRefs = useRef<Record<string, HTMLElement | null>>({});
  const concernRefs = useRef<Record<string, HTMLElement | null>>({});
  const isPatient = useAppSelector((state) => state.authentication.role === UserRoleEnum.Patient);

  // Why this concern isn't normally bookable for the patient (gender/age/
  // clearances/subscriber-only delivery). Only computed for staff — patients
  // never see ineligible concerns (they're filtered out of accessibleConcerns
  // below). is_subscribed is the staff-side subscriber signal (cf. DocumentDetails).
  const reasonFor = (concern: AppointmentConcern): string | undefined =>
    isPatient
      ? undefined
      : AppointmentUtils.getConcernIneligibilityReason(patient, concern, concernRequirements, {
          isSubscriber: !!patient?.is_subscribed,
        });

  const accessibleConcerns = useMemo(
    () =>
      (concerns || [])
        .filter(
          (c) =>
            !c.disabled &&
            (!isPatient || AppointmentUtils.hasAccessToConcern(patient, c, concernRequirements)),
        )
        .sort((a, b) => (a.text || '').localeCompare(b.text || '')),
    [concerns, patient, concernRequirements, isPatient],
  );

  const folders = useMemo(
    () => (childNodes || []).filter((n) => n.option.id && availableOptionIds.has(n.option.id)),
    [childNodes, availableOptionIds],
  );

  const openFolder = folders.find((f) => f.option.id === openFolderId) || null;
  const openConcern = accessibleConcerns.find((c) => c.id === openConcernId) || null;

  const closeOpenSubmenus = () => {
    setOpenFolderId(null);
    setOpenConcernId(null);
  };

  const handleConcernPick = (concern: AppointmentConcern) => {
    const opts = (concern.delivery_options || []) as any[];
    if (withDeliveryOptions && opts.length > 1) {
      setOpenFolderId(null);
      setOpenConcernId(concern.id!);
      return;
    }
    const deliveryOption = withDeliveryOptions ? opts[0] : undefined;
    onPick(concern.id!, {
      optionId: parentOptionId,
      deliveryOption,
      ineligibleReason: reasonFor(concern),
    });
  };

  const renderFolderItem = (node: TreeNode) => (
    <MenuItem
      key={node.option.id}
      ref={(el: HTMLElement | null) => {
        folderRefs.current[node.option.id!] = el;
      }}
      onMouseEnter={() => {
        setOpenConcernId(null);
        setOpenFolderId(node.option.id!);
      }}
      onClick={() => {
        setOpenConcernId(null);
        setOpenFolderId(node.option.id!);
      }}
      selected={openFolderId === node.option.id}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      {node.option.icon && (
        <Icon
          width="20px"
          height="20px"
          className="blue"
          src={node.option.icon}
          style={{ marginRight: 12 }}
        />
      )}
      <span style={{ flex: 1, marginRight: 16 }}>{node.option.title || node.option.text}</span>
      <span style={{ opacity: 0.6 }}>›</span>
    </MenuItem>
  );

  const renderConcernItem = (concern: AppointmentConcern) => {
    const opts = (concern.delivery_options || []) as any[];
    const expandable = withDeliveryOptions && opts.length > 1;
    const ineligibleReason = reasonFor(concern);

    const label = (
      <span
        style={{
          flex: 1,
          marginRight: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: ineligibleReason ? '#c77700' : undefined,
        }}
      >
        {ineligibleReason && (
          <Icon
            src="triangle-exclamation"
            width="14px"
            height="14px"
            iconStyle={{ color: '#e0972a' }}
          />
        )}
        {formatConcernLabel(concern)}
      </span>
    );

    return (
      <MenuItem
        key={concern.id}
        ref={(el: HTMLElement | null) => {
          concernRefs.current[concern.id!] = el;
        }}
        onMouseEnter={() => {
          if (expandable) {
            setOpenFolderId(null);
            setOpenConcernId(concern.id!);
          } else {
            closeOpenSubmenus();
          }
        }}
        onClick={() => handleConcernPick(concern)}
        selected={openConcernId === concern.id}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {ineligibleReason ? (
          <DefaultTooltip title={ineligibleReason} placement="right">
            {label}
          </DefaultTooltip>
        ) : (
          label
        )}
        {expandable && <span style={{ opacity: 0.6 }}>›</span>}
      </MenuItem>
    );
  };

  const folderSubmenu = openFolder && folderRefs.current[openFolder.option.id!] && (
    <ConcernCascadeLevel
      key={`folder-${openFolder.option.id}`}
      isRoot={false}
      anchorEl={folderRefs.current[openFolder.option.id!] || null}
      onClose={onClose}
      childNodes={openFolder.children}
      concerns={openFolder.concerns}
      parentOptionId={openFolder.option.id}
      patient={patient}
      concernRequirements={concernRequirements}
      availableOptionIds={availableOptionIds}
      onPick={onPick}
      withDeliveryOptions={withDeliveryOptions}
    />
  );

  const deliverySubmenu = openConcern && concernRefs.current[openConcern.id!] && (
    <DeliveryOptionsSubmenu
      key={`delivery-${openConcern.id}`}
      anchorEl={concernRefs.current[openConcern.id!] || null}
      concern={openConcern}
      onPick={(deliveryOption) => {
        onPick(openConcern.id!, {
          optionId: parentOptionId,
          deliveryOption,
          ineligibleReason: reasonFor(openConcern),
        });
      }}
    />
  );

  if (isRoot) {
    return (
      <>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          autoFocus={false}
          disableAutoFocusItem
        >
          {folders.map(renderFolderItem)}
        </Menu>
        {folderSubmenu}
        {deliverySubmenu}
      </>
    );
  }

  return (
    <Popper
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      placement="right-start"
      style={{ zIndex: 1500 }}
      modifiers={{
        flip: { enabled: true, boundariesElement: 'viewport' },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport',
          padding: 8,
        },
      }}
    >
      <Paper elevation={8} style={{ maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}>
        <MenuList disablePadding>
          {folders.map(renderFolderItem)}
          {accessibleConcerns.map(renderConcernItem)}
        </MenuList>
      </Paper>
      {folderSubmenu}
      {deliverySubmenu}
    </Popper>
  );
};

interface DeliveryOptionsSubmenuProps {
  anchorEl: HTMLElement | null;
  concern: AppointmentConcern;
  onPick: (deliveryOption: any) => void;
}

const DeliveryOptionsSubmenu: FC<DeliveryOptionsSubmenuProps> = ({ anchorEl, concern, onPick }) => {
  const opts = ((concern as any).delivery_options || []) as any[];
  return (
    <Popper
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      placement="right-start"
      style={{ zIndex: 1500 }}
      modifiers={{
        flip: { enabled: true, boundariesElement: 'viewport' },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport',
          padding: 8,
        },
      }}
    >
      <Paper elevation={8} style={{ maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}>
        <MenuList disablePadding>
          {opts.map((opt, index) => (
            <MenuItem key={index} onClick={() => onPick(opt)}>
              {formatDeliveryLabel(opt)}
              {opt.hidden_from_patient ? ' (hidden from patient)' : ''}
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    </Popper>
  );
};

export default BookingConcernPicker;
